import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import FullScreenLoader from "@/components/FullScreenLoader";
import { ArrowLeft, Loader2, Search } from "lucide-react";

export default function PriceSheetEditor() {
  const { sid } = useParams();
  const navigate = useNavigate();
  const [sheet, setSheet] = useState(null);
  const [name, setName] = useState("");
  const [prices, setPrices] = useState({}); // product_id -> string
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/store/price-sheets/${sid}`).then((r) => {
      setSheet(r.data);
      setName(r.data.name);
      const map = {};
      r.data.items.forEach((it) => { if (it.price != null) map[it.product_id] = String(it.price); });
      setPrices(map);
    });
  }, [sid]);

  if (!sheet) return <FullScreenLoader />;

  const setPrice = (pid, val) => setPrices((p) => ({ ...p, [pid]: val }));

  const save = async () => {
    setSaving(true);
    const priceList = Object.entries(prices)
      .filter(([, v]) => v !== "" && v != null && !isNaN(parseFloat(v)))
      .map(([product_id, v]) => ({ product_id, price: parseFloat(v) }));
    try {
      await api.put(`/store/price-sheets/${sid}`, { name, prices: priceList });
      toast.success("Price sheet saved");
      navigate("/dashboard/price-sheets");
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const items = sheet.items.filter((it) => it.name.toLowerCase().includes(search.toLowerCase()) || (it.sku || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6 max-w-3xl">
      <button onClick={() => navigate("/dashboard/price-sheets")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-sheets">
        <ArrowLeft className="h-4 w-4" /> Back to price sheets
      </button>

      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div className="flex-1 max-w-sm space-y-2">
          <label className="text-sm font-medium">Name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="h-11 text-lg font-semibold" data-testid="sheet-name-input" />
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">{sheet.customer_count} customer{sheet.customer_count === 1 ? "" : "s"} assigned</div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products..." className="pl-9 h-11" data-testid="sheet-product-search" />
      </div>

      <Card className="rounded-xl border-border divide-y divide-border">
        <div className="flex items-center justify-between px-5 py-3 text-xs uppercase tracking-wide text-muted-foreground font-medium">
          <span>Product</span><span>Sheet price (blank = base)</span>
        </div>
        {items.map((it) => (
          <div key={it.product_id} className="flex items-center justify-between gap-3 px-5 py-3">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{it.name}</p>
              <p className="text-xs text-muted-foreground">Base {money(it.base_price)}</p>
            </div>
            <Input type="number" step="0.01" value={prices[it.product_id] ?? ""} onChange={(e) => setPrice(it.product_id, e.target.value)}
              placeholder={String(it.base_price)} className="h-9 w-32" data-testid={`sheet-price-${it.product_id}`} />
          </div>
        ))}
        {items.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">No products found.</p>}
      </Card>

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate("/dashboard/price-sheets")}>Cancel</Button>
        <Button onClick={save} disabled={saving} className="rounded-lg" data-testid="save-sheet-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save price sheet"}
        </Button>
      </div>
    </div>
  );
}
